import React, { useState, useRef } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import {
  Trash2, Plus, Minus, Table2, MoreHorizontal,
  AlignLeft, AlignCenter, AlignRight,
  ArrowUpToLine, FoldVertical, ArrowDownToLine,
  ArrowRightToLine, ArrowDownFromLine, Split,
  GripVertical, ChevronUp, ChevronDown,
} from 'lucide-react';

const uid = () => Math.random().toString(36).substring(2, 10);

const normCell = (cell) => (typeof cell === 'object' && cell !== null) ? cell : { text: cell || '' };

const autoGrow = (el) => {
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight + 2, 400) + 'px';
};

// Compute column width percentages from CALS colwidth values (star or mm)
const colWeights = (colspecs) => {
  const w = colspecs.map(cs => {
    const v = parseFloat(cs.colwidth) || 1;
    return v > 0 ? v : 1;
  });
  const total = w.reduce((a, b) => a + b, 0) || 1;
  return w.map(x => (x / total) * 100);
};

/* ═══════════════════════════════════════════
   CALS TABLE EDITOR
   S1000D-compliant CALS table: colspec widths reflected in view, drag
   resize, per-cell align/valign (header propagates), merge cells,
   warning/caution/note in cells, removable header row.
   ═══════════════════════════════════════════ */
export const CalsTableEditor = ({ element, onUpdate, onDelete, compact = false }) => {
  const cols = element.cols || 2;
  const colspecs = element.colspecs || Array.from({ length: cols }, (_, i) => ({ colname: `col${i + 1}`, colwidth: '1*' }));
  const thead = element.thead || [];
  const tbody = element.tbody || [Array(cols).fill('')];
  const showTitle = element.title !== undefined;
  const [menuCell, setMenuCell] = useState(null); // {section, ri, ci}
  const tableRef = useRef(null);

  const emit = (updates) => onUpdate({ ...element, ...updates });
  const pct = colWeights(colspecs);

  // ── Column operations ──
  const insertColAt = (pos) => {
    const newCols = cols + 1;
    const insertRow = (row) => {
      const next = [...row];
      // if a horizontal span crosses the insert position, extend it and add a merged placeholder
      let spanOwner = -1;
      for (let ci = 0; ci < pos && ci < next.length; ci++) {
        const cell = normCell(next[ci]);
        if (!cell.merged && (cell.hspan || 1) > 1 && ci + (cell.hspan || 1) > pos) spanOwner = ci;
      }
      if (spanOwner >= 0) {
        const owner = normCell(next[spanOwner]);
        next[spanOwner] = { ...owner, hspan: (owner.hspan || 1) + 1 };
        next.splice(pos, 0, { merged: true });
      } else {
        next.splice(pos, 0, '');
      }
      return next;
    };
    const newSpecs = [...colspecs];
    newSpecs.splice(pos, 0, { colwidth: '1*' });
    emit({
      cols: newCols,
      colspecs: newSpecs.map((cs, i) => ({ ...cs, colname: `col${i + 1}` })),
      thead: thead.map(insertRow),
      tbody: tbody.map(insertRow),
    });
  };

  const deleteColAt = (pos) => {
    if (cols <= 1) return;
    const newCols = cols - 1;
    const cutRow = (row) => {
      const next = row.map((c, ci) => {
        const cell = normCell(c);
        const h = cell.hspan || 1;
        // shrink spans that cover the removed column
        if (ci < pos && ci + h > pos) {
          const nh = h - 1;
          const { hspan, ...rest } = cell;
          return nh > 1 ? { ...rest, hspan: nh } : rest;
        }
        return c;
      });
      next.splice(pos, 1);
      // clamp any remaining overflow spans
      return next.map((c, ci) => {
        const cell = normCell(c);
        const h = cell.hspan || 1;
        if (h > 1 && ci + h > newCols) {
          const nh = newCols - ci;
          const { hspan, ...rest } = cell;
          return nh > 1 ? { ...rest, hspan: nh } : rest;
        }
        return c;
      });
    };
    emit({
      cols: newCols,
      colspecs: colspecs.filter((_, i) => i !== pos).map((cs, i) => ({ ...cs, colname: `col${i + 1}` })),
      thead: thead.map(cutRow),
      tbody: tbody.map(cutRow),
    });
  };

  const addCol = () => insertColAt(cols);

  const removeCol = () => deleteColAt(cols - 1);

  // ── Row operations ──
  const addRow = () => emit({ tbody: [...tbody, Array(cols).fill('')] });
  const removeRow = (idx) => {
    if (tbody.length <= 1) return;
    emit({ tbody: tbody.filter((_, i) => i !== idx) });
  };
  const removeHeaderRow = () => emit({ thead: [] });
  const addHeaderRow = () => emit({ thead: [Array(cols).fill('')] });

  // ── Row reorder (merge-aware: vertically merged rows move as one block) ──
  const rowBlocks = React.useMemo(() => {
    const blocks = [];
    let r = 0;
    while (r < tbody.length) {
      let end = r;
      for (let rr = r; rr <= end && rr < tbody.length; rr++) {
        (tbody[rr] || []).forEach(c => {
          const m = normCell(c).morerows || 0;
          if (rr + m > end) end = Math.min(rr + m, tbody.length - 1);
        });
      }
      blocks.push({ start: r, end });
      r = end + 1;
    }
    return blocks;
  }, [tbody]);

  const [dragBlockIdx, setDragBlockIdx] = useState(null);
  const [dropBlockIdx, setDropBlockIdx] = useState(null);

  const blockIndexOfRow = (ri) => rowBlocks.findIndex(b => ri >= b.start && ri <= b.end);

  const moveBlockTo = (fromBi, toBi) => {
    if (fromBi === toBi || fromBi < 0 || toBi < 0 || fromBi >= rowBlocks.length || toBi >= rowBlocks.length) return;
    const blockRows = rowBlocks.map(b => tbody.slice(b.start, b.end + 1));
    const [moved] = blockRows.splice(fromBi, 1);
    blockRows.splice(toBi, 0, moved);
    emit({ tbody: blockRows.flat() });
  };

  const moveRowBlock = (ri, dir) => {
    const bi = blockIndexOfRow(ri);
    moveBlockTo(bi, bi + dir);
  };

  // ── Cell helpers ──
  const getRows = (section) => section === 'thead' ? thead : tbody;
  const setRows = (section, rows) => emit(section === 'thead' ? { thead: rows } : { tbody: rows });

  const updateCell = (section, ri, ci, updater) => {
    const rows = getRows(section).map((row, r) =>
      r === ri ? row.map((c, i) => (i === ci ? updater(normCell(c)) : c)) : [...row]
    );
    setRows(section, rows);
  };

  const setCellField = (section, ri, ci, field, value) =>
    updateCell(section, ri, ci, cell => {
      const next = { ...cell, [field]: value };
      if (value === undefined) delete next[field];
      return next;
    });

  // ── Merge operations (tbody only) ──
  const canMergeRight = (ri, ci) => {
    const cell = normCell(tbody[ri]?.[ci]);
    const h = cell.hspan || 1;
    const target = tbody[ri]?.[ci + h];
    if (target === undefined) return false;
    const t = normCell(target);
    return !t.merged && !(t.morerows > 0);
  };

  const canMergeDown = (ri, ci) => {
    const cell = normCell(tbody[ri]?.[ci]);
    const h = cell.hspan || 1;
    const m = cell.morerows || 0;
    const targetRow = tbody[ri + m + 1];
    if (!targetRow) return false;
    for (let k = 0; k < h; k++) {
      const t = normCell(targetRow[ci + k]);
      if (k === 0 && ((t.hspan || 1) !== h)) return false;
      if (t.merged || (k > 0 && !t.merged && (t.hspan || 1) !== 1 && k !== 0)) { /* covered below */ }
      if (t.morerows > 0) return false;
      if (k > 0 && !t.merged) return false;
      if (k === 0 && t.merged) return false;
    }
    return true;
  };

  const mergeRight = (ri, ci) => {
    if (!canMergeRight(ri, ci)) return;
    const rows = tbody.map(r => [...r]);
    const cell = normCell(rows[ri][ci]);
    const h = cell.hspan || 1;
    const target = normCell(rows[ri][ci + h]);
    const targetSpan = target.hspan || 1;
    const mergedText = [cell.text, target.text].filter(Boolean).join(' ');
    rows[ri][ci] = { ...cell, text: mergedText, hspan: h + targetSpan };
    for (let k = 0; k < targetSpan; k++) rows[ri][ci + h + k] = { merged: true };
    // If this cell spans rows, consume the same columns in covered rows
    const m = cell.morerows || 0;
    for (let r = ri + 1; r <= ri + m; r++) {
      for (let k = 0; k < targetSpan; k++) rows[r][ci + h + k] = { merged: true };
    }
    setRows('tbody', rows);
  };

  const mergeDown = (ri, ci) => {
    if (!canMergeDown(ri, ci)) return;
    const rows = tbody.map(r => [...r]);
    const cell = normCell(rows[ri][ci]);
    const h = cell.hspan || 1;
    const m = cell.morerows || 0;
    const tr = ri + m + 1;
    const target = normCell(rows[tr][ci]);
    const mergedText = [cell.text, target.text].filter(Boolean).join(' ');
    rows[ri][ci] = { ...cell, text: mergedText, morerows: m + 1 };
    for (let k = 0; k < h; k++) rows[tr][ci + k] = { merged: true };
    setRows('tbody', rows);
  };

  const unmergeCell = (ri, ci) => {
    const rows = tbody.map(r => [...r]);
    const cell = normCell(rows[ri][ci]);
    const h = cell.hspan || 1;
    const m = cell.morerows || 0;
    for (let r = ri; r <= ri + m; r++) {
      for (let k = 0; k < h; k++) {
        if (r === ri && k === 0) continue;
        if (rows[r] && rows[r][ci + k] !== undefined) rows[r][ci + k] = '';
      }
    }
    const next = { ...cell };
    delete next.hspan; delete next.morerows;
    rows[ri][ci] = next;
    setRows('tbody', rows);
  };

  // ── Column drag-resize ──
  const startResize = (ci, e) => {
    e.preventDefault();
    e.stopPropagation();
    const table = tableRef.current;
    if (!table || ci >= cols - 1) return;
    const total = table.getBoundingClientRect().width || 1;
    const widths = pct.map(p => (p / 100) * total);
    const startX = e.clientX;
    const onMove = (ev) => {
      const d = ev.clientX - startX;
      const w = [...widths];
      const pair = widths[ci] + widths[ci + 1];
      w[ci] = Math.min(Math.max(36, widths[ci] + d), pair - 36);
      w[ci + 1] = pair - w[ci];
      const sum = w.reduce((a, b) => a + b, 0);
      const newSpecs = colspecs.map((cs, i) => ({ ...cs, colwidth: `${Math.max(1, Math.round((w[i] / sum) * 100))}*` }));
      onUpdate({ ...element, colspecs: newSpecs });
    };
    const onUp = () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };

  // Effective alignment for a body cell (header propagation)
  const headerCellFor = (ci) => (thead.length > 0 ? normCell(thead[0][ci]) : {});
  const effAlign = (section, cell, ci) => cell.align || (section === 'tbody' ? headerCellFor(ci).align : undefined);
  const effValign = (section, cell, ci) => cell.valign || (section === 'tbody' ? headerCellFor(ci).valign : undefined);

  const cellTaClass = compact
    ? 'text-xs px-1.5 py-1 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-300 w-full resize-none overflow-hidden min-h-[26px]'
    : 'text-sm px-2 py-1.5 border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-violet-300 w-full resize-none overflow-hidden min-h-[32px]';

  const blockColors = {
    warning: 'bg-red-50 border-red-300 text-red-800',
    caution: 'bg-amber-50 border-amber-300 text-amber-800',
    note: 'bg-blue-50 border-blue-300 text-blue-800',
  };

  const renderCellBlocks = (section, ri, ci, cell) => (
    ['warning', 'caution', 'note'].filter(k => cell[k] !== undefined).map(k => (
      <div key={k} className={`border rounded px-1.5 py-1 ${blockColors[k]} space-y-0.5`}>
        <div className="flex items-center justify-between">
          <span className="text-[9px] font-bold uppercase">{k}</span>
          <button
            onClick={() => setCellField(section, ri, ci, k, undefined)}
            className="flex items-center gap-0.5 px-1 py-0.5 rounded hover:bg-red-100 hover:text-red-600"
            title={`Remove ${k} from this cell`}
            data-testid={`cell-remove-${k}-${section}-${ri}-${ci}-${element.id}`}
          ><Trash2 className="w-3 h-3" /><span className="text-[9px]">Remove</span></button>
        </div>
        <textarea
          ref={autoGrow}
          value={cell[k]}
          onChange={e => setCellField(section, ri, ci, k, e.target.value)}
          onInput={e => autoGrow(e.target)}
          rows={1}
          placeholder={`${k} text...`}
          className="w-full text-xs bg-white/70 rounded px-1 py-0.5 resize-none overflow-hidden focus:outline-none"
          data-testid={`cell-${k}-${section}-${ri}-${ci}-${element.id}`}
        />
      </div>
    ))
  );

  const renderMenu = (section, ri, ci, cell) => {
    const isBody = section === 'tbody';
    // Cell block rules: warning only if nothing else; caution only if no para/note
    const hasText = !!(cell.text || '').trim();
    const warningAllowed = cell.warning === undefined && cell.caution === undefined && cell.note === undefined && !hasText;
    const cautionAllowed = cell.caution === undefined && cell.note === undefined && !hasText;
    const alignBtn = (val, Icon, title) => (
      <button key={val} onClick={() => setCellField(section, ri, ci, 'align', cell.align === val ? undefined : val)}
        className={`p-1 rounded ${cell.align === val ? 'bg-violet-100 text-violet-700' : 'text-slate-500 hover:bg-slate-100'}`}
        title={title} data-testid={`cell-align-${val}-${section}-${ri}-${ci}-${element.id}`}>
        <Icon className="w-3.5 h-3.5" />
      </button>
    );
    const valignBtn = (val, Icon, title) => (
      <button key={val} onClick={() => setCellField(section, ri, ci, 'valign', cell.valign === val ? undefined : val)}
        className={`p-1 rounded ${cell.valign === val ? 'bg-violet-100 text-violet-700' : 'text-slate-500 hover:bg-slate-100'}`}
        title={title} data-testid={`cell-valign-${val}-${section}-${ri}-${ci}-${element.id}`}>
        <Icon className="w-3.5 h-3.5" />
      </button>
    );
    return (
      <>
        <div className="fixed inset-0 z-40" onClick={() => setMenuCell(null)} data-testid={`cell-menu-backdrop-${element.id}`} />
        <div
          className="fixed z-50 bg-white border border-slate-200 rounded-lg shadow-lg p-2 space-y-1.5 min-w-[210px]"
          style={{ top: menuCell.y, left: menuCell.x }}
          data-testid={`cell-menu-${section}-${ri}-${ci}-${element.id}`}>
        <div className="flex items-center gap-0.5">
          <span className="text-[9px] uppercase text-slate-400 w-10">Align</span>
          {alignBtn('left', AlignLeft, 'Align left')}
          {alignBtn('center', AlignCenter, 'Align center')}
          {alignBtn('right', AlignRight, 'Align right')}
          <span className="text-[9px] uppercase text-slate-400 w-12 ml-2">Valign</span>
          {valignBtn('top', ArrowUpToLine, 'Align top')}
          {valignBtn('middle', FoldVertical, 'Align middle')}
          {valignBtn('bottom', ArrowDownToLine, 'Align bottom')}
        </div>
        {section === 'thead' && (
          <p className="text-[9px] text-slate-400">Header alignment applies to all cells in this column</p>
        )}
        {isBody && (
          <div className="flex items-center gap-1 border-t border-slate-100 pt-1.5">
            <span className="text-[9px] uppercase text-slate-400 w-10">Merge</span>
            <button onClick={() => { mergeRight(ri, ci); setMenuCell(null); }} disabled={!canMergeRight(ri, ci)}
              className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Merge with cell to the right"
              data-testid={`merge-right-${ri}-${ci}-${element.id}`}>
              <ArrowRightToLine className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => { mergeDown(ri, ci); setMenuCell(null); }} disabled={!canMergeDown(ri, ci)}
              className="p-1 rounded text-slate-500 hover:bg-slate-100 disabled:opacity-30" title="Merge with cell below"
              data-testid={`merge-down-${ri}-${ci}-${element.id}`}>
              <ArrowDownFromLine className="w-3.5 h-3.5" />
            </button>
            {((cell.hspan || 1) > 1 || (cell.morerows || 0) > 0) && (
              <button onClick={() => { unmergeCell(ri, ci); setMenuCell(null); }}
                className="p-1 rounded text-red-500 hover:bg-red-50" title="Unmerge"
                data-testid={`unmerge-${ri}-${ci}-${element.id}`}>
                <Split className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        )}
        {isBody && (
          <div className="flex items-center gap-1 border-t border-slate-100 pt-1.5">
            <span className="text-[9px] uppercase text-slate-400 w-10">Add</span>
            <button onClick={() => { setCellField(section, ri, ci, 'warning', ''); setMenuCell(null); }} disabled={!warningAllowed}
              className="text-[10px] px-1.5 py-0.5 rounded border border-red-200 text-red-600 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
              title={warningAllowed ? 'Add warning (must be first content in the cell)' : 'Warning must be the first content in the cell'}
              data-testid={`cell-add-warning-${ri}-${ci}-${element.id}`}>Warning</button>
            <button onClick={() => { setCellField(section, ri, ci, 'caution', ''); setMenuCell(null); }} disabled={!cautionAllowed}
              className="text-[10px] px-1.5 py-0.5 rounded border border-amber-200 text-amber-600 hover:bg-amber-50 disabled:opacity-30 disabled:cursor-not-allowed"
              title={cautionAllowed ? 'Add caution' : 'Caution can only be added before para/note content'}
              data-testid={`cell-add-caution-${ri}-${ci}-${element.id}`}>Caution</button>
            <button onClick={() => { setCellField(section, ri, ci, 'note', ''); setMenuCell(null); }} disabled={cell.note !== undefined}
              className="text-[10px] px-1.5 py-0.5 rounded border border-blue-200 text-blue-600 hover:bg-blue-50 disabled:opacity-30"
              data-testid={`cell-add-note-${ri}-${ci}-${element.id}`}>Note</button>
          </div>
        )}
        </div>
      </>
    );
  };

  const renderCell = (section, ri, ci, raw, Tag) => {
    const cell = normCell(raw);
    if (cell.merged) return null;
    const hspan = cell.hspan || 1;
    const rowspan = (cell.morerows || 0) + 1;
    const align = effAlign(section, cell, ci);
    const valign = effValign(section, cell, ci);
    const isMenuOpen = menuCell && menuCell.section === section && menuCell.ri === ri && menuCell.ci === ci;
    const vAlignCls = valign === 'middle' ? 'align-middle' : valign === 'bottom' ? 'align-bottom' : 'align-top';
    return (
      <Tag key={ci} colSpan={hspan > 1 ? hspan : undefined} rowSpan={rowspan > 1 ? rowspan : undefined}
        className={`p-0.5 relative ${vAlignCls}`} data-testid={`cell-${section}-${ri}-${ci}-${element.id}`}>
        <div className="relative group/cell space-y-1">
          {section === 'tbody' && renderCellBlocks(section, ri, ci, cell)}
          <textarea
            ref={autoGrow}
            value={cell.text || ''}
            onChange={e => setCellField(section, ri, ci, 'text', e.target.value)}
            onInput={e => autoGrow(e.target)}
            rows={1}
            placeholder={section === 'thead' ? `Header ${ci + 1}` : ''}
            style={{ textAlign: align || 'left' }}
            className={`${cellTaClass} ${section === 'thead' ? 'font-semibold bg-violet-50' : 'bg-white'} ${(hspan > 1 || rowspan > 1) ? 'ring-1 ring-violet-300' : ''}`}
            data-testid={`${section}-${ri}-${ci}-${element.id}`}
          />
          <button
            onClick={(e) => {
              if (isMenuOpen) { setMenuCell(null); return; }
              const r = e.currentTarget.getBoundingClientRect();
              const menuH = section === 'tbody' ? 170 : 90;
              setMenuCell({
                section, ri, ci,
                x: Math.max(8, Math.min(r.left - 180, window.innerWidth - 240)),
                y: r.bottom + menuH > window.innerHeight ? Math.max(8, r.top - menuH - 6) : r.bottom + 4,
              });
            }}
            className={`absolute top-1 right-1 p-0.5 rounded bg-white/90 border border-slate-200 text-slate-400 hover:text-violet-600 transition-opacity ${isMenuOpen ? 'opacity-100 text-violet-600' : 'opacity-0 group-hover/cell:opacity-100 focus:opacity-100'}`}
            title="Cell options (align, merge, warning/caution/note)"
            data-testid={`cell-menu-btn-${section}-${ri}-${ci}-${element.id}`}
          ><MoreHorizontal className="w-3 h-3" /></button>
          {isMenuOpen && renderMenu(section, ri, ci, cell)}
        </div>
      </Tag>
    );
  };

  return (
    <div className="border border-slate-200 rounded-lg bg-white group" data-testid={`cals-table-${element.id}`}>
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-t-lg border-b border-slate-200">
        <div className="flex items-center gap-2">
          <Table2 className="w-4 h-4 text-slate-500" />
          <Badge className="text-[10px] bg-slate-100 text-slate-700">CALS Table</Badge>
          <span className="text-xs text-slate-400">{cols} cols × {tbody.length} rows</span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={addCol} className="p-1 text-slate-400 hover:text-violet-600 rounded hover:bg-violet-50" title="Add column">
            <Plus className="w-3.5 h-3.5" />
          </button>
          <button onClick={removeCol} className="p-1 text-slate-400 hover:text-red-500 rounded hover:bg-red-50" title="Remove last column" disabled={cols <= 1}>
            <Minus className="w-3.5 h-3.5" />
          </button>
          {onDelete && (
            <button onClick={onDelete} className="p-1 text-slate-300 hover:text-red-500 ml-1" data-testid={`delete-table-${element.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
        </div>
      </div>

      <div className="p-3 space-y-3">
        {/* Table title (optional) */}
        {showTitle && (
          <div className="space-y-1">
            <Label className="text-xs text-slate-500">Table Title</Label>
            <Input
              value={element.title || ''}
              onChange={e => emit({ title: e.target.value })}
              placeholder="Table title (optional)..."
              className="text-sm h-8"
              data-testid={`table-title-${element.id}`}
            />
          </div>
        )}

        {/* Column widths + per-column insert/delete */}
        <div className="flex gap-0.5 items-center flex-wrap">
          <span className="text-[10px] text-slate-400 w-16 shrink-0">Columns:</span>
          <button onClick={() => insertColAt(0)} className="px-0.5 text-slate-300 hover:text-violet-600" title="Insert column here" data-testid={`insert-col-0-${element.id}`}>
            <Plus className="w-3 h-3" />
          </button>
          {colspecs.map((cs, ci) => (
            <React.Fragment key={ci}>
              <div className="flex items-center gap-0.5 border border-slate-200 rounded px-1 py-0.5 bg-slate-50">
                <input
                  value={cs.colwidth}
                  onChange={e => emit({ colspecs: colspecs.map((c, i) => i === ci ? { ...c, colwidth: e.target.value } : c) })}
                  className="text-[10px] w-12 text-center font-mono text-slate-500 bg-transparent focus:outline-none"
                  title={`Width for ${cs.colname} — drag column borders in the grid to resize`}
                  data-testid={`colwidth-${ci}-${element.id}`}
                />
                <button onClick={() => deleteColAt(ci)} disabled={cols <= 1}
                  className="text-slate-300 hover:text-red-500 disabled:opacity-30" title={`Delete column ${ci + 1}`}
                  data-testid={`delete-col-${ci}-${element.id}`}>
                  <Trash2 className="w-2.5 h-2.5" />
                </button>
              </div>
              <button onClick={() => insertColAt(ci + 1)} className="px-0.5 text-slate-300 hover:text-violet-600" title="Insert column here" data-testid={`insert-col-${ci + 1}-${element.id}`}>
                <Plus className="w-3 h-3" />
              </button>
            </React.Fragment>
          ))}
          <span className="text-[9px] text-slate-300 ml-1">drag column borders to resize</span>
        </div>

        {/* Table grid */}
        <div className="overflow-x-auto pb-1">
          <table ref={tableRef} className="w-full border-collapse" style={{ tableLayout: 'fixed' }} data-testid={`table-grid-${element.id}`}>
            <colgroup>
              {pct.map((p, i) => <col key={i} style={{ width: `${p}%` }} />)}
              <col style={{ width: '52px' }} />
            </colgroup>
            {/* Resize handle row */}
            <thead>
              <tr className="h-1.5">
                {colspecs.map((cs, ci) => (
                  <th key={ci} className="relative p-0 h-1.5">
                    {ci < cols - 1 && (
                      <div
                        onMouseDown={e => startResize(ci, e)}
                        className="absolute -right-1 top-0 w-2 h-1.5 cursor-col-resize rounded bg-slate-200 hover:bg-violet-400"
                        title="Drag to resize columns"
                        data-testid={`col-resizer-${ci}-${element.id}`}
                      />
                    )}
                  </th>
                ))}
                <th className="p-0" />
              </tr>
              {thead.map((row, ri) => (
                <tr key={`h-${ri}`} className="bg-violet-50">
                  {row.map((cell, ci) => renderCell('thead', ri, ci, cell, 'th'))}
                  <th className="w-7 text-center">
                    <button onClick={removeHeaderRow} className="p-0.5 text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 transition-opacity" title="Delete header row" data-testid={`delete-header-row-${element.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </th>
                </tr>
              ))}
            </thead>
            {/* Body */}
            <tbody>
              {tbody.map((row, ri) => {
                const bi = blockIndexOfRow(ri);
                const block = rowBlocks[bi];
                const isBlockStart = block && block.start === ri;
                const isDropTarget = dropBlockIdx === bi && dragBlockIdx !== null && dragBlockIdx !== bi;
                return (
                <tr
                  key={`b-${ri}`}
                  className={`hover:bg-slate-50/50 ${isDropTarget ? 'outline outline-2 outline-violet-400 bg-violet-50/40' : ''}`}
                  onDragOver={(e) => { if (dragBlockIdx !== null) { e.preventDefault(); setDropBlockIdx(bi); } }}
                  onDragLeave={() => { if (dropBlockIdx === bi) setDropBlockIdx(null); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    if (dragBlockIdx !== null) moveBlockTo(dragBlockIdx, bi);
                    setDragBlockIdx(null); setDropBlockIdx(null);
                  }}
                  data-testid={`table-row-${ri}-${element.id}`}
                >
                  {row.map((cell, ci) => renderCell('tbody', ri, ci, cell, 'td'))}
                  <td className="text-center align-top pt-1">
                    {isBlockStart && (
                      <div className="flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <span
                          draggable
                          onDragStart={(e) => { e.dataTransfer.effectAllowed = 'move'; setDragBlockIdx(bi); }}
                          onDragEnd={() => { setDragBlockIdx(null); setDropBlockIdx(null); }}
                          className="p-0.5 text-slate-300 hover:text-violet-600 cursor-grab active:cursor-grabbing"
                          title={block.end > block.start ? 'Drag to reorder (merged rows move together)' : 'Drag to reorder row'}
                          data-testid={`drag-row-${ri}-${element.id}`}
                        ><GripVertical className="w-3 h-3" /></span>
                        <div className="flex flex-col">
                          <button onClick={() => moveRowBlock(ri, -1)} disabled={bi === 0}
                            className="p-0 text-slate-300 hover:text-violet-600 disabled:opacity-20" title="Move row up"
                            data-testid={`move-row-up-${ri}-${element.id}`}>
                            <ChevronUp className="w-3 h-3" />
                          </button>
                          <button onClick={() => moveRowBlock(ri, 1)} disabled={bi === rowBlocks.length - 1}
                            className="p-0 text-slate-300 hover:text-violet-600 disabled:opacity-20" title="Move row down"
                            data-testid={`move-row-down-${ri}-${element.id}`}>
                            <ChevronDown className="w-3 h-3" />
                          </button>
                        </div>
                        <button onClick={() => removeRow(ri)} className="p-0.5 text-slate-300 hover:text-red-500" title="Delete row">
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Add row / header row */}
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-slate-500" onClick={addRow} data-testid={`add-row-${element.id}`}>
            <Plus className="w-3 h-3" /> Add Row
          </Button>
          {thead.length === 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-6 gap-1 text-violet-600" onClick={addHeaderRow} data-testid={`add-header-row-${element.id}`}>
              <Plus className="w-3 h-3" /> Add Header Row
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════
   PARSE CALS TABLE from DOM <table> element
   ═══════════════════════════════════════════ */
const directParaText = (entry) =>
  Array.from(entry.querySelectorAll(':scope > para')).map(p => p.textContent?.trim() || '').join('\n');

function parseEntryCell(entry, colsAttr, nameToIdx, cursor) {
  const namest = entry.getAttribute('namest');
  const nameend = entry.getAttribute('nameend');
  const colname = entry.getAttribute('colname');
  let start = cursor;
  if (namest && nameToIdx[namest] !== undefined) start = nameToIdx[namest];
  else if (colname && nameToIdx[colname] !== undefined) start = nameToIdx[colname];
  let hspan = 1;
  if (namest && nameend && nameToIdx[nameend] !== undefined) {
    hspan = Math.max(1, nameToIdx[nameend] - start + 1);
  }
  const cell = { text: directParaText(entry) };
  const morerows = parseInt(entry.getAttribute('morerows') || '0', 10);
  if (morerows > 0) cell.morerows = morerows;
  if (hspan > 1) cell.hspan = hspan;
  const align = entry.getAttribute('align');
  const valign = entry.getAttribute('valign');
  if (align) cell.align = align;
  if (valign) cell.valign = valign;
  const w = entry.querySelector(':scope > warning warningAndCautionPara');
  if (w) cell.warning = Array.from(entry.querySelectorAll(':scope > warning > warningAndCautionPara')).map(x => x.textContent?.trim() || '').join('\n');
  const c = entry.querySelector(':scope > caution warningAndCautionPara');
  if (c) cell.caution = Array.from(entry.querySelectorAll(':scope > caution > warningAndCautionPara')).map(x => x.textContent?.trim() || '').join('\n');
  const n = entry.querySelector(':scope > note notePara');
  if (n) cell.note = Array.from(entry.querySelectorAll(':scope > note > notePara')).map(x => x.textContent?.trim() || '').join('\n');
  // Simple text-only cell → keep legacy string format
  const keys = Object.keys(cell).filter(k => cell[k] !== undefined && cell[k] !== '');
  const isPlain = keys.length === 0 || (keys.length === 1 && keys[0] === 'text');
  return { start, hspan, cell: isPlain ? (cell.text || '') : cell };
}

function parseSectionRows(sectionEl, colsAttr, nameToIdx) {
  const rows = [];
  const pending = {}; // colIndex -> {left, hspan}
  sectionEl.querySelectorAll(':scope > row').forEach(rowEl => {
    const cells = Array(colsAttr).fill(null);
    Object.keys(pending).forEach(k => {
      const ci = parseInt(k, 10);
      if (pending[k].left > 0) {
        for (let j = 0; j < pending[k].hspan; j++) {
          if (ci + j < colsAttr) cells[ci + j] = { merged: true };
        }
      }
    });
    let cursor = 0;
    rowEl.querySelectorAll(':scope > entry').forEach(entry => {
      while (cursor < colsAttr && cells[cursor] !== null) cursor++;
      const { start, hspan, cell } = parseEntryCell(entry, colsAttr, nameToIdx, cursor);
      if (start < colsAttr) {
        cells[start] = cell;
        for (let j = 1; j < hspan; j++) {
          if (start + j < colsAttr) cells[start + j] = { merged: true };
        }
        const mr = (typeof cell === 'object' && cell.morerows) || 0;
        if (mr > 0) pending[start] = { left: mr + 1, hspan };
        cursor = start + hspan;
      }
    });
    for (let i = 0; i < colsAttr; i++) if (cells[i] === null) cells[i] = '';
    Object.keys(pending).forEach(k => {
      pending[k].left -= 1;
      if (pending[k].left <= 0) delete pending[k];
    });
    rows.push(cells);
  });
  return rows;
}

export function parseCalsTable(tableEl) {
  const titleEl = tableEl.querySelector(':scope > title');
  const tgroup = tableEl.querySelector(':scope > tgroup');
  const colsAttr = tgroup ? parseInt(tgroup.getAttribute('cols') || '2', 10) : 2;

  const colspecs = [];
  const nameToIdx = {};
  if (tgroup) {
    tgroup.querySelectorAll(':scope > colspec').forEach(cs => {
      const spec = {
        colname: cs.getAttribute('colname') || `col${colspecs.length + 1}`,
        colwidth: cs.getAttribute('colwidth') || '1*',
      };
      const align = cs.getAttribute('align');
      if (align) spec.align = align;
      nameToIdx[spec.colname] = colspecs.length;
      colspecs.push(spec);
    });
  }
  while (colspecs.length < colsAttr) {
    const name = `col${colspecs.length + 1}`;
    nameToIdx[name] = colspecs.length;
    colspecs.push({ colname: name, colwidth: '1*' });
  }

  const theadEl = tgroup?.querySelector(':scope > thead');
  let thead = theadEl ? parseSectionRows(theadEl, colsAttr, nameToIdx) : [];
  // Apply colspec align to first header row cells (propagation source)
  if (thead.length > 0) {
    thead = thead.map((row, ri) => ri !== 0 ? row : row.map((cell, ci) => {
      const csAlign = colspecs[ci]?.align;
      if (!csAlign) return cell;
      const c = normCell(cell);
      if (c.merged || c.align) return cell;
      return { ...c, align: csAlign };
    }));
  }

  const tbodyEl = tgroup?.querySelector(':scope > tbody');
  const tbody = tbodyEl ? parseSectionRows(tbodyEl, colsAttr, nameToIdx) : [Array(colsAttr).fill('')];

  return {
    id: tableEl.getAttribute('id') || uid(),
    type: 'table',
    title: titleEl?.textContent?.trim() || '',
    cols: colsAttr,
    colspecs: colspecs.map(({ align, ...rest }) => rest),
    thead,
    tbody: tbody.length > 0 ? tbody : [Array(colsAttr).fill('')],
  };
}
